import React, { useMemo, useState } from 'react';
import {
  CalendarCheck,
  CalendarClock,
  CircleDot,
  AlertTriangle,
  CalendarDays,
  CircleSlash,
  XCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Clock,
} from 'lucide-react';
import { useTasks } from '../../../../context/TasksContext';
import { projectPlanWithDrift } from '../../../../utils/buildForecast';
import {
  isInactiveAccount,
  isClosedAccount,
} from '../../../../utils/accountLifecycle';

const DEFAULT_VISIBLE = 5;
const HORIZON_DAYS = 180;

const STATUS_STYLES = {
  Completed:    'bg-gray-100 text-gray-600 border-gray-200',
  Scheduled:    'bg-emerald-50 text-emerald-700 border-emerald-200',
  Today:        'bg-sky-50 text-sky-700 border-sky-200',
  'Late today': 'bg-amber-50 text-amber-800 border-amber-200',
  Overdue:      'bg-amber-100 text-amber-900 border-amber-300',
  Stale:        'bg-red-50 text-red-700 border-red-200',
  Expected:     'bg-sky-50 text-sky-700 border-sky-200',
  Drifted:      'bg-amber-50 text-amber-800 border-amber-200',
};

const STATUS_DOT = {
  Completed:    'bg-gray-400',
  Scheduled:    'bg-emerald-500',
  Today:        'bg-sky-500',
  'Late today': 'bg-amber-500',
  Overdue:      'bg-amber-600',
  Stale:        'bg-red-500',
  Expected:     'bg-sky-500',
  Drifted:      'bg-amber-500',
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = typeof iso === 'string' ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatRelative(iso) {
  if (!iso) return '';
  const target = typeof iso === 'string' ? new Date(`${iso}T12:00:00`) : new Date(iso);
  if (Number.isNaN(target.getTime())) return '';
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const diffDays = Math.round((target - today) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays > 0 && diffDays < 14) return `in ${diffDays} days`;
  if (diffDays > 0 && diffDays < 60) return `in ${Math.round(diffDays / 7)} weeks`;
  if (diffDays > 0) return `in ~${Math.round(diffDays / 30)} months`;
  if (diffDays === -1) return 'yesterday';
  if (diffDays > -14) return `${Math.abs(diffDays)} days ago`;
  return `${Math.round(Math.abs(diffDays) / 7)} weeks ago`;
}

function intervalLabel(days) {
  if (!days) return '';
  if (days === 7)  return 'Weekly';
  if (days === 14) return 'Bi-Weekly';
  if (days === 21) return 'Every 3 weeks';
  if (days === 30) return 'Monthly';
  if (days === 42) return 'Every 6 weeks';
  if (days === 60) return 'Every 60 days';
  if (days === 90) return 'Quarterly';
  return `Every ${days} days`;
}

/**
 * Map a scheduled row to a status label string used by STATUS_STYLES / DOT.
 * Overdue / Stale / Late today take precedence over the plain Scheduled label.
 */
function scheduledStatusLabel(proj) {
  if (proj.staleness === 'stale')      return 'Stale';
  if (proj.staleness === 'overdue')    return 'Overdue';
  if (proj.staleness === 'late_today') return 'Late today';
  // Same calendar day but no staleness flag → "Today" affordance.
  const today = new Date(); today.setHours(0,0,0,0);
  if (proj.scheduledDate) {
    const d = new Date(`${proj.scheduledDate}T00:00:00`);
    if (d.getTime() === today.getTime()) return 'Today';
  }
  return 'Scheduled';
}

/**
 * ServiceOutlookCard — Option A read-only projection of upcoming recurring
 * visits for a single lead. No Firestore writes. No new tasks created.
 * Uses the existing buildForecast pipeline to stay consistent with the
 * company-wide /forecast page.
 *
 * Multi-Program Phase 2 restore: optional `tasksOverride` prop replaces the
 * TasksContext feed. When passed, the caller has already pre-filtered the
 * task set to a specific enrollment's scope and constructed a synthetic
 * lead-view whose `.recurrence` reflects that enrollment. This keeps
 * projections and drift-detection scoped correctly per enrollment without
 * modifying `buildForecast` internals.
 */
const ServiceOutlookCard = ({ client, embedded = false, tasksOverride = null }) => {
  const { tasks: tasksFromContext } = useTasks();
  const tasks = tasksOverride || tasksFromContext;
  const [expanded, setExpanded] = useState(false);

  const {
    projections,
    summary,
    drift,
    duplicates,
    overdueCount,
    staleCount,
    cursorMismatch,
    isActive,
  } = useMemo(
    () => projectPlanWithDrift(client, tasks, HORIZON_DAYS),
    [client, tasks]
  );

  if (!isActive) return null; // no active recurrence → no card

  const inactive = isInactiveAccount(client);
  const closed = isClosedAccount(client);

  const visibleProjections = expanded ? projections : projections.slice(0, DEFAULT_VISIBLE);
  const hiddenCount = Math.max(0, projections.length - DEFAULT_VISIBLE);

  // Status decision for each row.
  const rowStatus = (proj, idx) => {
    if (proj.isScheduled) return scheduledStatusLabel(proj);
    // Drifted: only mark the FIRST row when drift exists (the next-expected).
    if (drift && idx === 0) return 'Drifted';
    return 'Expected';
  };

  const hasDuplicates = duplicates && duplicates.length > 0;
  const hasOverdueLike = overdueCount > 0 || staleCount > 0;

  // When embedded inside ProgramCard the outer card chrome + Service Outlook
  // header are owned by the parent's collapsible disclosure. Strip them
  // here so the layout reads as a single unified section.
  const wrapperClass = embedded
    ? 'space-y-4'
    : `bg-white rounded-lg border border-gray-200 p-4 space-y-4 ${closed ? 'shadow-none' : ''}`;
  const wrapperStyle = (!embedded && closed) ? { filter: 'saturate(0.85)' } : undefined;

  return (
    <div
      className={wrapperClass}
      data-testid="service-outlook-card"
      style={wrapperStyle}
    >
      {/* Header — suppressed in embedded mode (ProgramCard owns the heading) */}
      {!embedded && (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900">Service Outlook</h3>
          </div>
          <span className="text-[11px] text-gray-400">{intervalLabel(summary.intervalDays)}</span>
        </div>
      )}

      {/* Lifecycle banners */}
      {inactive && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-slate-100 border border-slate-200 text-slate-700 text-xs"
          data-testid="service-outlook-inactive-banner"
        >
          <CircleSlash className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>
            Account is <strong>Inactive</strong>.
            Automated visits are suppressed; the outlook below shows what would resume.
          </span>
        </div>
      )}
      {closed && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-gray-50 border border-gray-200 text-gray-700 text-xs"
          data-testid="service-outlook-closed-banner"
        >
          <XCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-gray-500" />
          <span>
            Account is <strong>Closed</strong> — the projection is frozen.
            History remains visible for reference.
          </span>
        </div>
      )}

      {/* Overdue / stale banner — top-level operator nudge */}
      {hasOverdueLike && !closed && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
          data-testid="service-outlook-overdue-banner"
        >
          <Clock className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-700" />
          <span>
            {staleCount > 0 && (
              <>
                <strong>{staleCount}</strong> scheduled visit{staleCount === 1 ? ' is' : 's are'} more
                than a week past their date and need review.{' '}
              </>
            )}
            {overdueCount > 0 && (
              <>
                <strong>{overdueCount}</strong> visit{overdueCount === 1 ? ' is' : 's are'} overdue.{' '}
              </>
            )}
            Confirm completion or reschedule before relying on this projection.
          </span>
        </div>
      )}

      {/* Duplicate scheduled-task warning */}
      {hasDuplicates && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-xs"
          data-testid="service-outlook-duplicate-banner"
        >
          <Copy className="h-3.5 w-3.5 mt-0.5 shrink-0 text-red-600" />
          <span>
            {duplicates.map((d) => (
              <span key={d.taskIds.join('-') || d.dateKey} className="block">
                Duplicate scheduled tasks detected for{' '}
                {d.visitIndex
                  ? <>Visit #<strong>{d.visitIndex}</strong></>
                  : <>visit on <strong>{d.dateKey || 'unknown date'}</strong></>}
                {' '}— {d.count} non-terminal tasks exist. Review schedule.
              </span>
            ))}
          </span>
        </div>
      )}

      {/* Cursor-mismatch caption (Service Plan cursor vs live calendar) */}
      {cursorMismatch && !closed && (
        <div
          className="text-[11px] text-gray-500 px-1"
          data-testid="service-outlook-cursor-mismatch"
        >
          Next visit shown reflects the calendar
          {' '}({formatDate(cursorMismatch.liveDate)}).
          Plan cursor ({formatDate(cursorMismatch.cursorDate)}) will sync after completion.
        </div>
      )}

      {/* Drift warning */}
      {drift && !inactive && !closed && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-900 text-xs"
          data-testid="service-outlook-drift-banner"
        >
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
          <span>
            Schedule is running ~{drift.driftDays} day{drift.driftDays === 1 ? '' : 's'} behind
            projected cadence.
          </span>
        </div>
      )}

      {/* Season summary strip */}
      <div className="grid grid-cols-3 gap-2 text-xs" data-testid="service-outlook-summary">
        <SummaryStat
          label={summary.total != null ? 'Remaining' : 'Completed'}
          value={
            summary.total != null
              ? `${summary.remaining}/${summary.total}`
              : `${summary.completed}`
          }
          sublabel={summary.total != null ? 'visits' : 'visits done'}
        />
        <SummaryStat
          label="Next"
          value={formatDate(summary.nextDate)}
          sublabel={formatRelative(summary.nextDate)}
        />
        <SummaryStat
          label={summary.estimatedSeasonEnd ? 'Season ends' : 'Cadence'}
          value={
            summary.estimatedSeasonEnd
              ? `~${formatDate(summary.estimatedSeasonEnd)}`
              : intervalLabel(summary.intervalDays)
          }
          sublabel={summary.estimatedSeasonEnd ? 'at this pace' : 'ongoing'}
        />
      </div>

      {/* Visit list */}
      {projections.length === 0 ? (
        <div className="text-xs text-gray-500 italic px-1 py-3 text-center">
          No upcoming visits projected within the next {HORIZON_DAYS} days.
        </div>
      ) : (
        <ol className="space-y-1.5" data-testid="service-outlook-visits">
          {visibleProjections.map((proj, idx) => {
            const status = rowStatus(proj, idx);
            return (
              <li
                key={`v-${proj.visitIndex}-${proj.projectedDate}-${proj.taskId || idx}`}
                className="flex items-center gap-3 px-2.5 py-1.5 rounded border border-gray-100 hover:bg-gray-50 transition-colors"
                data-testid={`service-outlook-row-${proj.visitIndex}`}
              >
                <span className="text-xs font-medium text-gray-700 w-14 shrink-0">
                  Visit #{proj.visitIndex}
                </span>
                <span className="text-xs text-gray-700 w-16 shrink-0">
                  {formatDate(proj.isScheduled && proj.scheduledDate ? proj.scheduledDate : proj.projectedDate)}
                </span>
                <span className="text-[11px] text-gray-500 flex-1 truncate">
                  {formatRelative(proj.isScheduled && proj.scheduledDate ? proj.scheduledDate : proj.projectedDate)}
                </span>
                <span
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${STATUS_STYLES[status] || STATUS_STYLES.Scheduled}`}
                  data-testid={`service-outlook-status-${proj.visitIndex}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status] || STATUS_DOT.Scheduled}`} />
                  {status}
                </span>
              </li>
            );
          })}
        </ol>
      )}

      {/* Expander */}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="w-full flex items-center justify-center gap-1 text-xs text-gray-500 hover:text-gray-700 py-1.5 rounded hover:bg-gray-50 transition-colors"
          data-testid="service-outlook-toggle"
        >
          {expanded ? (
            <>Show less <ChevronUp className="h-3 w-3" /></>
          ) : (
            <>Show all {projections.length} visits <ChevronDown className="h-3 w-3" /></>
          )}
        </button>
      )}

      {/* Footer legend (compact) */}
      <div className="flex items-center gap-3 text-[10px] text-gray-400 pt-1 border-t border-gray-100 flex-wrap">
        <Legend dot="bg-emerald-500" label="Scheduled" />
        <Legend dot="bg-sky-500" label="Expected" />
        {overdueCount > 0 && <Legend dot="bg-amber-600" label="Overdue" />}
        {staleCount > 0   && <Legend dot="bg-red-500"    label="Stale" />}
        {drift            && <Legend dot="bg-amber-500"  label="Drifted" />}
        <span className="ml-auto">Read-only projection</span>
      </div>
    </div>
  );
};

const SummaryStat = ({ label, value, sublabel }) => (
  <div className="bg-gray-50 rounded-md px-2.5 py-2 min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-gray-400">{label}</div>
    <div className="text-sm font-semibold text-gray-900 truncate">{value}</div>
    {sublabel && <div className="text-[10px] text-gray-500 truncate">{sublabel}</div>}
  </div>
);

const Legend = ({ dot, label }) => (
  <span className="inline-flex items-center gap-1">
    <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
    {label}
  </span>
);

// Suppress unused-icon warnings — reserved for future status variants.
void CalendarCheck;
void CalendarClock;
void CircleDot;

export default ServiceOutlookCard;
